import { FC, useEffect, useRef, useState } from "react";
import Image from "next/image";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { Badge } from "@src/lib/ui/Badge";
import { Button } from "@src/lib/ui/Button";
import { Tooltip, TooltipContent, TooltipTrigger } from "@src/lib/ui/Tooltip";
import { textV4Variants } from "@src/lib/ui/Text";
import XIcon from "@src/lib/icons/12px/x";
import { Dialog, DialogContent, DialogTitle } from "@src/lib/ui/Dialog";
import { waitForConfetti } from "@src/lib/utils/confetti";
import {
  EnvelopeBack,
  EnvelopeFlap,
  EnvelopePocket,
  FLAP_HEIGHT,
} from "./EnvelopeArt";
import ReleaseLetter, { RELEASE_PAPER_CLASSNAME } from "./ReleaseLetter";
import { celebrateFromEnvelope } from "./releaseConfetti";
import { ReleaseHeadlineRelease } from "./releases";
import { cn } from "@src/lib/utils/cn";

type Stage = "sealed" | "opening" | "letter";

// Timeline of the opening animation. framer-motion takes seconds; timers take
// milliseconds.
const FLAP_DURATION_S = 0.45;
const LETTER_RISE_DELAY_S = 0.35;
const CONFETTI_DELAY_MS = 450;
const LETTER_REVEAL_DELAY_MS = 1300;
// framer-motion needs the radius in `style` to keep corners round while the
// paper scales between the note and the letter.
const NOTE_RADIUS = { borderRadius: 10 };
const LETTER_RADIUS = { borderRadius: 16 };
const PAPER_MORPH = {
  type: "tween",
  duration: 0.45,
  ease: [0.22, 1, 0.36, 1],
} as const;
// The letter's content waits for the paper to finish growing, so the note's
// header and the letter never show at once.
const LETTER_CONTENT_DELAY_S = 0.22;
// The envelope lands, wiggles once, then opens by itself.
const AUTO_OPEN_DELAY_MS = 1900;

// The top of the release card as it looks peeking out of the envelope.
const NoteHeader: FC<{ release: ReleaseHeadlineRelease }> = ({ release }) => (
  <span className="flex flex-col items-start gap-3 text-left">
    <Image src="/images/opus-wordmark.svg" alt="" width={52} height={14} />
    <span
      className={cn(
        textV4Variants({ variant: "page-title" }),
        "text-balance text-text-primary",
      )}
    >
      {release.headline}
    </span>
  </span>
);

// An envelope that drops onto the dashboard and opens by itself: the flap
// swings up, a note rises out with confetti, and the note unfolds into the
// full announcement. It can be closed once the letter is showing.
const ReleaseEnvelope: FC<{
  release: ReleaseHeadlineRelease;
  onOpen: () => void;
  onClose: () => void;
}> = ({ release, onOpen, onClose }) => {
  const [stage, setStage] = useState<Stage>("sealed");
  // Once the flap has swung past vertical it tucks behind the letter.
  const [flapBehind, setFlapBehind] = useState(false);
  const envelopeRef = useRef<HTMLButtonElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const timers = useRef<ReturnType<typeof setTimeout>[]>([]);
  const cancelCelebration = useRef<() => void>(() => undefined);
  const reduceMotion = useReducedMotion();
  const headingId = `release-letter-${release.slug}`;
  // The note that rises out of the envelope and the letter share this layout
  // id, so the note's paper grows straight into the letter.
  const paperId = `release-paper-${release.slug}`;
  const started = useRef(false);

  const schedule = (callback: () => void, delay: number) => {
    timers.current.push(setTimeout(callback, delay));
  };

  const open = () => {
    if (started.current) return;
    started.current = true;
    onOpen();
    if (reduceMotion) {
      setStage("letter");
      return;
    }
    setStage("opening");
    schedule(() => setFlapBehind(true), (FLAP_DURATION_S * 1000) / 2);
    schedule(async () => {
      if (!(await waitForConfetti()) || !envelopeRef.current) return;
      cancelCelebration.current = celebrateFromEnvelope(
        envelopeRef.current.getBoundingClientRect(),
      );
    }, CONFETTI_DELAY_MS);
    schedule(() => setStage("letter"), LETTER_REVEAL_DELAY_MS);
  };

  useEffect(() => {
    const pending = timers.current;
    // Don't use up the moment in a background tab: start the countdown once
    // the page is visible.
    const startCountdown = () => {
      if (document.visibilityState !== "visible") return;
      document.removeEventListener("visibilitychange", startCountdown);
      schedule(open, reduceMotion ? 0 : AUTO_OPEN_DELAY_MS);
    };
    document.addEventListener("visibilitychange", startCountdown);
    startCountdown();
    return () => {
      document.removeEventListener("visibilitychange", startCountdown);
      pending.forEach(clearTimeout);
      cancelCelebration.current();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- run once on mount
  }, []);

  // The envelope button unmounts as the letter appears; keep focus in the
  // dialog itself (as the agent gallery does) rather than ringing Close.
  useEffect(() => {
    if (stage === "letter") contentRef.current?.focus({ preventScroll: true });
  }, [stage]);

  const isOpen = stage !== "sealed";
  const blockUntilRead = (event: Event) => {
    if (stage !== "letter") event.preventDefault();
  };

  return (
    <Dialog
      open
      onOpenChange={(nextOpen) => {
        if (!nextOpen && stage === "letter") onClose();
      }}
    >
      <DialogContent
        ref={contentRef}
        hideCloseButton
        hideDefaultTitle
        aria-describedby={undefined}
        // Keep focus inside the dialog without drawing a focus ring on the
        // envelope before anyone has interacted with it.
        onOpenAutoFocus={(event) => {
          event.preventDefault();
          contentRef.current?.focus();
        }}
        onEscapeKeyDown={blockUntilRead}
        onInteractOutside={blockUntilRead}
        data-testid="release-envelope"
        className="w-[min(960px,calc(100vw-32px))] max-w-none justify-items-center border-0 bg-transparent p-0 shadow-none outline-none sm:rounded-none"
      >
        <DialogTitle className="sr-only">
          Your {release.month} release note
        </DialogTitle>
        <AnimatePresence>
          {stage !== "letter" ? (
            <motion.div
              key="envelope"
              className="col-start-1 row-start-1 flex flex-col items-center gap-8"
              initial={reduceMotion ? false : { y: -320, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={
                reduceMotion
                  ? { opacity: 0 }
                  : {
                      y: 140,
                      opacity: 0,
                      transition: { duration: 0.4, ease: "easeIn" },
                    }
              }
              transition={{ type: "spring", stiffness: 160, damping: 16 }}
            >
              <motion.div
                aria-hidden
                // On its own surface so it reads over the scrim in both themes.
                className="flex flex-col items-center gap-2 rounded-xl bg-bg-elevated px-5 py-3 text-center shadow-large"
                animate={{ opacity: isOpen ? 0 : 1 }}
              >
                <Badge variant="toneNeutral">New in Opus</Badge>
                <span
                  className={cn(
                    textV4Variants({ variant: "page-title" }),
                    "text-text-primary",
                  )}
                >
                  {release.month}’s feature release is here
                </span>
              </motion.div>

              <motion.button
                ref={envelopeRef}
                type="button"
                onClick={open}
                aria-label={`Open your ${release.month} release note`}
                data-testid="release-envelope-open"
                // ds-allow: the flap's 3D turn needs a perspective distance
                className="relative aspect-[460/300] w-[min(460px,calc(100vw-48px))] cursor-pointer rounded-xl shadow-extra-large perspective-[1200px] focus-visible:ring-1 focus-visible:ring-border-tertiary focus-visible:outline-none"
                animate={
                  isOpen || reduceMotion
                    ? { rotate: 0 }
                    : { rotate: [0, -3, 3, -2, 2, 0] }
                }
                transition={
                  isOpen || reduceMotion
                    ? { duration: 0.2 }
                    : { duration: 0.8, delay: 0.7 }
                }
              >
                <EnvelopeBack className="absolute inset-0 z-0 size-full" />

                <motion.span
                  // Sealed, the note stays hidden so it can't show past the flap's edges.
                  className={cn(
                    "absolute inset-x-[5%] top-[5%] z-10 flex h-[86%] flex-col px-6 pt-6",
                    !isOpen && "invisible",
                  )}
                  animate={{ y: isOpen ? "-52%" : "0%" }}
                  transition={{
                    delay: LETTER_RISE_DELAY_S,
                    duration: 0.55,
                    ease: [0.2, 0.9, 0.3, 1.2],
                  }}
                >
                  <motion.span
                    layoutId={reduceMotion ? undefined : paperId}
                    className={cn(
                      "absolute inset-0 -z-10",
                      RELEASE_PAPER_CLASSNAME,
                    )}
                    style={NOTE_RADIUS}
                  />
                  <NoteHeader release={release} />
                </motion.span>

                <EnvelopePocket className="absolute inset-0 z-20 size-full overflow-visible" />

                <motion.span
                  aria-hidden
                  className="absolute inset-x-0 top-0 origin-top transform-3d"
                  style={{
                    zIndex: flapBehind ? 5 : 30,
                    height: `${(FLAP_HEIGHT / 300) * 100}%`,
                  }}
                  animate={{ rotateX: isOpen ? 180 : 0 }}
                  transition={{ duration: FLAP_DURATION_S, ease: "easeInOut" }}
                >
                  <EnvelopeFlap />
                </motion.span>
              </motion.button>
            </motion.div>
          ) : (
            <motion.section
              key="letter"
              aria-labelledby={headingId}
              data-testid="release-letter"
              className="relative z-10 col-start-1 row-start-1 w-full max-w-5xl self-center"
            >
              <motion.div
                layoutId={reduceMotion ? undefined : paperId}
                aria-hidden
                className={cn(
                  "absolute inset-0 shadow-2xl",
                  RELEASE_PAPER_CLASSNAME,
                )}
                style={LETTER_RADIUS}
                // Grow as solid paper instead of fading in over the envelope.
                layoutCrossfade={false}
                transition={PAPER_MORPH}
              >
                {!reduceMotion && (
                  // The note's header fades as the paper starts to grow, before
                  // the letter's own content fades in.
                  <motion.div
                    layout
                    className="absolute top-6 left-6"
                    initial={{ opacity: 1 }}
                    animate={{ opacity: 0 }}
                    transition={{
                      layout: PAPER_MORPH,
                      opacity: { duration: 0.12 },
                    }}
                  >
                    <NoteHeader release={release} />
                  </motion.div>
                )}
              </motion.div>
              <motion.div
                className="absolute top-3 right-3 z-20"
                initial={reduceMotion ? false : { opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{
                  delay: reduceMotion ? 0 : LETTER_CONTENT_DELAY_S,
                  duration: 0.25,
                }}
              >
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="secondary"
                      size="icon-sm"
                      onClick={onClose}
                      aria-label="Close"
                    >
                      <XIcon />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>Close</TooltipContent>
                </Tooltip>
              </motion.div>
              <motion.div
                // Tall enough for the feature list beside its preview.
                className="relative isolate h-[min(85vh,680px)] overflow-hidden rounded-2xl"
                initial={reduceMotion ? false : { opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{
                  delay: reduceMotion ? 0 : LETTER_CONTENT_DELAY_S,
                  duration: 0.3,
                  ease: "easeOut",
                }}
              >
                <ReleaseLetter release={release} headingId={headingId} />
              </motion.div>
            </motion.section>
          )}
        </AnimatePresence>
      </DialogContent>
    </Dialog>
  );
};

export default ReleaseEnvelope;
